/* eslint-disable react-hooks/exhaustive-deps */
import React, { lazy, useContext, useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import MolGrid, { Column } from "@moltech/molui/molgrid";

/** Utilities */
import formatDate from "../../../../components/finalMolGrid/libs/formatDate";
import {
  customRowsPerPageDropdown,
  getDropdownValue,
} from "../../../../components/finalForms/libs/data/dropdownUtilities";

/** Contexts */
import { ServiceLocationContext } from "../../../../utils/context/ServiceLocationContext";

/** Common Components */
import Buttons from "../../../../components/ui/button/Buttons";
import CardSection from "../../../../components/ui/card/CardSection";
import FormCreator from "../../../../components/finalForms/FormCreator";
import CustomCenterModel from "../../../../components/ui/customCenterModel/CustomCenterModel";

/** Common Services & Data Files */
import { AppIcons } from "../../../../data/appIcons";

/** Configuration files */
import { OutstandingSearchFilter } from "./components/OutstandingSearchFilter.data";

/**Wrapped Component */
import Financial from "../../Financial";

/** RTK Query */
import { useGetMolGridDataMutation } from "../../../../app/services/commonAPI";
import { useExportOutstandingInvoicesMutation } from "../../../../app/services/financialsAPI";

/** CSS Files */
import "./OutstandingInvoices.scss";

/** Lazily Loaded Components */
const StatusAndFollowupDate = lazy(() =>
  import("./features/statusAndFollowup/StatusAndFollowupDate")
);
const ImportInvoice = lazy(() =>
  import("./features/importInvoice/ImportInvoice")
);

const OutstandingInvoices = () => {
  const searchFormRef = useRef(null);
  const gridRef = useRef();

  const { selectedLocation } = useContext(ServiceLocationContext);
  const locationkey = getDropdownValue(selectedLocation);
  const currentDate = formatDate(new Date(), "MM-DD-YYYY");
  const [isDisable, setIsDisable] = useState(true);

  const [searchFilter, setSearchFilter] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isImportModelOpen, setIsImportModelOpen] = useState(false);

  const [
    exportOutstandingInvoices,
    { isLoading: isExporting, isSuccess: isAllExported, data: exportAllData },
  ] = useExportOutstandingInvoicesMutation();

  // Export All Details response handling
  useEffect(() => {
    if (!isExporting && isAllExported && exportAllData) {
      const blob = new Blob([exportAllData]);
      saveAs(blob, `Outstanding Invoices ${currentDate}.xlsx`);
    }
  }, [isExporting, isAllExported, exportAllData]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.reload();
    }
  }, [searchFilter, locationkey]);

  const handleInputChange = (dataField, updatedData) => {
    setSearchFilter(updatedData?.sPurchaseOrder);
  };

  const beforeMolGridLoad = (reqPrm) => {
    const req = {
      pagination: {
        pageNumber: reqPrm.first / reqPrm.rows + 1,
        pageSize: reqPrm.rows,
      },
      filters: {
        searchText: searchFilter || "",
      },
      sortField: reqPrm.sortField,
      sortOrder: reqPrm.sortOrder,
      plServiceLocationKey: locationkey,
      groupBy: reqPrm.groupBy ? reqPrm.groupBy : "",
    };
    return req;
  };

  const afterMolGridLoad = (data) => {
    setIsDisable(false);
    return {
      data: data.dataSource,
      count: data.totalRecord,
    };
  };

  const handleExportClick = () => {
    const request = {
      plServiceLocationKey: locationkey,
      filters: {
        searchText: searchFilter || "",
      },
    };
    exportOutstandingInvoices(request);
  };

  const handleImportClick = () => {
    setIsImportModelOpen(true);
  };

  const headerContent = () => {
    return (
      <div className="d-flex justify-content-end gap-3 export-icon">
        <Buttons
          titleText={"Import"}
          isIcon={true}
          iconClass={"uil:export"}
          onClick={handleImportClick}
          buttonTypeClassName={"blue-btn"}
        />
        <div>
          <Buttons
            titleText={"Export"}
            isIcon={true}
            iconClass="file-icons:microsoft-excel"
            onClick={handleExportClick}
            buttonTypeClassName={"blue-btn"}
            isDisable={isDisable}
            isLoading={isExporting}
          />
        </div>
      </div>
    );
  };

  const handleStatusAndFollowupClick = (rowData) => {
    setIsModelOpen(true);
    setSelectedInvoice(rowData.lInvoiceKey);
  };

  const handleCloseModel = () => {
    setIsModelOpen(false);
    setSelectedInvoice(null);
  };

  const handleCloseImportModel = () => {
    setIsImportModelOpen(false);
  };

  const handleSuccessImport = () => {
    handleCloseImportModel();
    if (gridRef.current) {
      gridRef.current.reload();
    }
  };

  const handleSuccess = () => {
    if (gridRef.current) {
      gridRef.current.reload();
    }
    handleCloseModel();
  };

  const getCtxMenuItems = (row) => {
    if (!row) return [];

    return [
      {
        label: "Retrieve Payment from GP",
        //  icon: 'pi pi-plus',
        command: () => alert(`Retrieve Payment from GP`),
      },
      {
        label: "Status and Follow-up Date",
        //icon: 'pi pi-pen-to-square',
        command: () => handleStatusAndFollowupClick(row),
      },
    ];
  };

  return (
    <>
      <div className="supplier-po-table small-card-btn-sec head-center outstanding-list">
        <FormCreator
          ref={searchFormRef}
          config={OutstandingSearchFilter}
          onColumnChange={handleInputChange}
        />
        <CardSection
          cardTitle={`Outstanding Invoices`}
          customHeader={true}
          headerContent={headerContent()}
        >
          <div className="scrollable-grid">
            <MolGrid
              ref={gridRef}
              url="/Financials/GetOutstandingInvoicesList"
              client={useGetMolGridDataMutation}
              beforeLoad={beforeMolGridLoad}
              afterLoad={afterMolGridLoad}
              grouping
              expandableRowGroups={true}
              paginator
              rows={99999}
              rowsPerPageOptions={[10, 25, 50, 100]}
              // virtualScrollerOptions={{ itemSize: 18 }}
              paginatorTemplate={{
                layout:
                  "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
                RowsPerPageDropdown: (options) => {
                  return customRowsPerPageDropdown(options, options?.value);
                },
              }}
              scrollable
              scrollHeight="flex"
              filterDisplay="row"
              sortMode="single"
              showGridlines
              resizableColumns
              ctxMenuItems={getCtxMenuItems}
            >
              <Column
                field="sCompanyName1"
                header="Company"
                grouping
                sortable
              />
              <Column field="sClientName1" header="Client" grouping sortable />
              <Column
                field="sDepartmentName"
                header="Department"
                grouping
                sortable
              />
              <Column
                field="contractName"
                header="Contract Name"
                grouping
                sortable
              />
              <Column
                field="dtTranDate"
                header="Invoice Date"
                colType="datetime"
                format="mm/dd/yyyy"
                grouping
                bodyStyle={{ textAlign: "center" }}
                sortable
              />
              <Column
                field="sTranNumber"
                header="Work Order"
                bodyStyle={{ textAlign: "center" }}
                sortable
              />
              <Column
                field="sPurchaseOrder"
                header="Purchase Order"
                bodyStyle={{ textAlign: "center" }}
                sortable
              />
              <Column
                field="repName"
                header="Rep Name"
                grouping
                bodyStyle={{ textAlign: "center" }}
                sortable
              />
              <Column
                field="aging"
                header="Aging"
                bodyStyle={{ textAlign: "right" }}
                sortable
              />
              <Column
                field="balance"
                header="Balance"
                colType="money"
                bodyStyle={{ textAlign: "right" }}
                sortable
              />
            </MolGrid>
          </div>
        </CardSection>
      </div>
      <CustomCenterModel
        isOpen={isModelOpen}
        useCustomBg={true}
        modalTitle={"Invoice Status And Follow-up Date"}
        contentClass="content-35"
        onClose={handleCloseModel}
        modalTitleIcon={AppIcons.AddIcon}
      >
        <StatusAndFollowupDate
          isOpen={isModelOpen}
          onClose={handleCloseModel}
          onSuccess={handleSuccess}
          selectedInvoice={selectedInvoice}
        />
      </CustomCenterModel>

      <CustomCenterModel
        isOpen={isImportModelOpen}
        useCustomBg={true}
        modalTitle={"Import Outstanding Invoices"}
        contentClass="content-45"
        onClose={handleCloseImportModel}
        modalTitleIcon={AppIcons.AddIcon}
      >
        <ImportInvoice
          isOpen={isImportModelOpen}
          onClose={handleCloseImportModel}
          onSuccess={handleSuccessImport}
        />
      </CustomCenterModel>
    </>
  );
};
export default Financial(OutstandingInvoices);
